import React, { useLayoutEffect } from 'react';

/**
 * Runs `measure` on mount and whenever the element resizes or its child list
 * changes (swapping children doesn't resize the element's own box), plus once on
 * the next animation frame to catch post-layout content. Wires up a
 * ResizeObserver + MutationObserver on the ref and cleans them up.
 */
export function useOverflowMeasure(
  ref: React.RefObject<HTMLElement | null>,
  measure: () => void,
  deps: React.DependencyList,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    const raf = requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
